<script lang="ts" setup>
import {computed, ref} from 'vue'

const SALE_TYPE_DEFAULT = 1;
const SALE_TYPE_VISIT = 2;

const free = ref<boolean>(false)
const prices = [300, 500, 1000, 2000, 2500, 5000]
const amount = ref<number>(1000)
const quantity = ref<number>(1)
const visits = ref<number>(1)
const email = ref<string>("")
const promoCode = ref<string>("")
const promoCodeApplied = ref<boolean>(false)
const promoDiscount = ref<number>(0)
const acceptRules = ref<boolean>(false)
const loading = ref<boolean>(false)
const errors = ref([])
const props = defineProps<{
    tenant_id: number;
    tenant_name: string;
    tenant_bonus: number;
    sale_type: number;
    visit_cost: number;
    template_choices: {id: number; preview: string}[];
}>()
const templateId = ref<number | null>(props.template_choices[0]?.id ?? null)
const selectedTemplate = computed(() => props.template_choices.find(template => template.id === templateId.value) ?? null)

function _hasOwnProperty(obj, field) {
    return Object.prototype.hasOwnProperty.call(obj, field)
}

function ecommerce(event: string, price: number, orderId: string | null) {
    const data = {
        ecommerce: {
            currencyCode: "RUB",
        }
    }

    data['ecommerce'][event] = {
        actionField: {
            id: orderId,
            goal_id: 516960685,
            revenue: price,
        },
        products: [{
            price: price,
            quantity: quantity.value,
            visits: visits.value,
            position: 1,
            category: "Электронный сертификат",
            brand: props.tenant_name,
            name: `Электронный сертификат ${props.tenant_name} на сумму ${price} руб`,
        }]
    }

    window.dataLayer.push(data)
}

function createOrder() {
    if (!acceptRules.value) {
        errors.value['rule'] = ["Для оплаты сертификата необходимо ознакомиться и согласиться с Офертой И Политикой обработки персональных данных"]
        return
    }
    const form = new FormData()
    form.append("email", email.value)
    form.append("quantity", quantity.value)
    form.append("tenant_id", props.tenant_id)
    if (templateId.value !== null) {
        form.append("template_id", templateId.value)
    }
    if (promoCodeApplied.value) {
        form.append("promo_code", promoCode.value)
    }

    switch(props.sale_type) {
        case SALE_TYPE_DEFAULT:
            form.append("amount", amount.value)
            break;
        case SALE_TYPE_VISIT:
            form.append("visits", visits.value)
            break;
    }

    loading.value = true
    fetch('/order', {method: 'POST', body: form})
        .then(function (r) {
            if (r.ok) {
                return r.json()
            }
            alert('Внутрнення ошибка. Повторите позже')
        })
        .then(function (r) {
            ecommerce("purchase", amount.value, r.order_id)

            return r
        })
        .then(function (r) {
            if (_hasOwnProperty(r, 'location')) {
                window.location = r.location;
                return
            }
            if (_hasOwnProperty(r, 'errors')) {
                errors.value = r.errors
                return
            }
        })
        .finally(() => loading.value = false)

}

function applyPromoCode() {
    clearErrors('promo_code')
    if (!promoCode.value.trim()) {
        errors.value['promo_code'] = ['Введите промокод']
        return
    }

    const form = new FormData()
    form.append('tenant_id', props.tenant_id)
    form.append('promo_code', promoCode.value)
    loading.value = true
    fetch('/order/validate-promo-code', {method: 'POST', body: form})
        .then(r => r.json().then(data => ({ok: r.ok, data})))
        .then(({ok, data}) => {
            if (!ok) {
                errors.value = {...errors.value, ...data.errors}
                return
            }
            promoCodeApplied.value = true
            promoDiscount.value = data.discount_percent
        })
        .catch(() => {
            errors.value['promo_code'] = ['Не удалось проверить промокод. Повторите попытку.']
        })
        .finally(() => loading.value = false)
}

const cost = computed(() => {
    const subtotal = props.sale_type == SALE_TYPE_DEFAULT
        ? amount.value * quantity.value / (1 + props.tenant_bonus * 0.01)
        : props.visit_cost * visits.value * quantity.value
    return Math.ceil(subtotal * (100 - promoDiscount.value) / 100)
})

const amountErrorMessage = computed(() => {
    const messages = errors.value!.amount
    return messages ? messages.join(' ') : null
})
const quantityErrorMessage = computed(() => {
    const messages = errors.value!.quantity
    return messages ? messages.join(' ') : null
})
const visitsErrorMessage = computed(() => {
    const messages = errors.value!.visits
    return messages ? messages.join(' ') : null
})
const emailErrorMessage = computed(() => {
    const messages = errors.value!.email
    return messages ? messages.join(' ') : null
})
const ruleErrorMessage = computed(() => {
    const messages = errors.value!.rule
    return messages ? messages.join(' ') : null
})
const promoCodeErrorMessage = computed(() => {
    const messages = errors.value!.promo_code
    return messages ? messages.join(' ') : null
})
const templateErrorMessage = computed(() => {
    const messages = errors.value!.template_id
    return messages ? messages.join(' ') : null
})
const disabledCreateOrder = computed(() => {
    return loading.value || props.template_choices.length === 0 || templateId.value === null ||
        !acceptRules.value || !email.value || ruleErrorMessage.value || emailErrorMessage.value || templateErrorMessage.value ||
        amountErrorMessage.value || quantityErrorMessage.value || visitsErrorMessage.value ||
        (promoCode.value.trim() !== '' && !promoCodeApplied.value)
})

function clearErrors(field) {
    delete errors.value[field]
}

const currencyFormatter = new Intl.NumberFormat("ru-RU", {style: "currency", currency: "RUB", maximumFractionDigits: 0})

function setAmount(s) {
    amount.value = s
    free.value = false
}
</script>

<template>
    <div v-if="props.template_choices.length === 0" class="my-8 rounded-lg border border-amber-300 bg-amber-50 p-4 text-amber-950" role="alert">
        Покупка временно недоступна: для этого аккаунта пока нет активного фона сертификата.
    </div>
    <div v-else class="my-8 grid w-full gap-8 lg:my-10 lg:grid-cols-[minmax(0,1fr)_minmax(18rem,0.72fr)] lg:items-start">
        <div class="space-y-6">
            <fieldset class="space-y-3">
                <legend class="text-xl font-semibold">Выберите фон сертификата <span class="text-red-600">*</span></legend>
                <div class="grid grid-cols-2 gap-3 sm:grid-cols-3">
                    <button v-for="(template, index) in props.template_choices" :key="template.id"
                            :aria-pressed="templateId === template.id"
                            :class="templateId === template.id ? 'border-blue-700 ring-2 ring-blue-200' : 'border-slate-300 hover:border-slate-400'"
                            class="rounded-lg border bg-white p-2 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                            type="button"
                            @click="templateId = template.id; clearErrors('template_id')">
                        <img :src="template.preview" :alt="`Предпросмотр фона ${index + 1}`"
                             class="mx-auto max-h-56 w-auto rounded border border-slate-200 object-contain"/>
                        <span class="mt-2 block text-sm font-medium text-slate-800">Вариант {{ index + 1 }}</span>
                    </button>
                </div>
                <div v-if="templateErrorMessage" class="invalid-feedback" role="alert">{{ templateErrorMessage }}</div>
            </fieldset>

            <fieldset v-if="props.sale_type === SALE_TYPE_DEFAULT" class="space-y-3">
                <legend class="text-xl font-semibold">Выберите номинал</legend>
                <div class="flex flex-wrap gap-3">
                    <button v-for="price in prices" :key="price"
                            :aria-pressed="price === amount && !free"
                            :class="price === amount && !free ? 'border-blue-700 bg-blue-700 text-white' : 'border-slate-300 bg-white text-slate-800 hover:bg-slate-50'"
                            class="min-w-24 rounded-md border px-3 py-2 font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                            type="button" @click="setAmount(price)">{{ currencyFormatter.format(price) }}
                    </button>
                    <button :aria-pressed="free"
                            :class="free ? 'border-blue-700 bg-blue-700 text-white' : 'border-slate-300 bg-white text-slate-800 hover:bg-slate-50'"
                            class="min-w-24 rounded-md border px-3 py-2 font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                            type="button" @click="free = true">Свободная сумма
                    </button>
                </div>
            </fieldset>

            <template v-if="props.sale_type === SALE_TYPE_VISIT">
                <h2 class="text-xl font-semibold">Выберите количество посещений</h2>
                <div class="required">
                    <label class="mb-1 block text-sm font-medium text-slate-700" for="visits">Количество посещений <span class="text-red-600">*</span></label>
                    <input id="visits" v-model="visits" :class="{'is-invalid': visitsErrorMessage !== null}"
                           aria-required="true" class="form-control" required type="number"
                           @input="clearErrors('visits')"/>
                    <div v-if="visitsErrorMessage" class="invalid-feedback">{{ visitsErrorMessage }}</div>
                </div>
            </template>

            <div v-if="free">
                <label class="mb-1 block text-sm font-medium text-slate-700" for="amount">Сумма <span class="text-red-600">*</span></label>
                <input id="amount" v-model="amount" :class="{'is-invalid': amountErrorMessage !== null}"
                       aria-required="true" class="form-control" required type="text" @input="clearErrors('amount')"/>
                <div v-if="amountErrorMessage" class="invalid-feedback">{{ amountErrorMessage }}</div>
            </div>

            <div class="required">
                <label class="mb-1 block text-sm font-medium text-slate-700" for="quantity">Количество сертификатов <span class="text-red-600">*</span></label>
                <input id="quantity" v-model="quantity" :class="{'is-invalid': quantityErrorMessage !== null}"
                       aria-required="true" class="form-control" required type="number"
                       @input="clearErrors('quantity')"/>
                <div v-if="quantityErrorMessage" class="invalid-feedback">{{ quantityErrorMessage }}</div>
            </div>

            <div class="space-y-2">
                <label class="mb-1 block text-sm font-medium text-slate-700" for="promo-code">Промокод</label>
                <div class="flex flex-wrap gap-2">
                    <input id="promo-code" v-model="promoCode" :class="{'is-invalid': promoCodeErrorMessage !== null}"
                           class="form-control min-w-48 flex-1" type="text" autocomplete="off"
                           @input="promoCodeApplied = false; promoDiscount = 0; clearErrors('promo_code')"/>
                    <button class="site-button site-button-secondary" type="button" :disabled="loading || promoCodeApplied"
                            @click="applyPromoCode">{{ promoCodeApplied ? 'Применён' : 'Применить' }}</button>
                </div>
                <div v-if="promoCodeErrorMessage" class="invalid-feedback">{{ promoCodeErrorMessage }}</div>
            </div>

            <fieldset class="space-y-5">
                <legend class="text-xl font-semibold">Ваши контакты</legend>
                <div class="required">
                    <label class="mb-1 block text-sm font-medium text-slate-700" for="email">Ваш e-mail <span class="text-red-600">*</span></label>
                    <input id="email" v-model="email" :class="{'is-invalid': emailErrorMessage !== null}"
                           aria-describedby="email-hint" aria-required="true" class="form-control" required type="email"
                           @input="clearErrors('email')"/>
                    <p id="email-hint" class="mb-0 mt-1 text-sm leading-5 text-slate-600">
                        После успешной оплаты PDF-сертификат с уникальным кодом придёт на этот e-mail.
                    </p>
                    <div v-if="emailErrorMessage" class="invalid-feedback">{{ emailErrorMessage }}</div>
                </div>
                <div>
                    <div class="flex items-start gap-3">
                        <input id="accept-rules" v-model="acceptRules"
                               :class="{'is-invalid': ruleErrorMessage !== null}" class="form-check-input mt-0.5 shrink-0" role="switch"
                               type="checkbox"/>
                        <label class="text-sm leading-6 text-slate-700" for="accept-rules">Я соглашаюсь с
                            <a href="/rules/offer" target="_blank">Офертой</a> и
                            <a href="/rules/privacy" target="_blank">Политикой обработки персональных данных</a>
                        </label>
                    </div>
                    <div v-if="ruleErrorMessage" class="invalid-feedback">{{ ruleErrorMessage }}</div>
                </div>

                <div>
                    <button :disabled="disabledCreateOrder" class="site-button w-full sm:w-auto" type="button"
                            @click="createOrder">Оплатить
                    </button>
                </div>
            </fieldset>
        </div>

        <aside aria-label="Сводка заказа" class="hidden rounded-xl border border-slate-200 bg-white p-5 shadow-sm lg:sticky lg:top-6 lg:block sm:p-6">
            <h2 class="m-0 text-lg font-semibold text-slate-900">Ваш сертификат</h2>
            <div class="mt-4 flex min-h-40 items-center justify-center rounded-lg bg-slate-50 p-4">
                <img v-if="selectedTemplate" :src="selectedTemplate.preview" alt="Предпросмотр выбранного фона сертификата"
                     class="max-h-48 max-w-full rounded border border-slate-200 object-contain"/>
            </div>
            <dl class="mt-5 space-y-3 text-sm">
                <div v-if="props.sale_type === SALE_TYPE_DEFAULT" class="flex justify-between gap-4">
                    <dt class="text-slate-600">Номинал</dt>
                    <dd class="m-0 text-right font-medium text-slate-900">{{ currencyFormatter.format(amount) }}</dd>
                </div>
                <div v-if="props.sale_type === SALE_TYPE_VISIT" class="flex justify-between gap-4">
                    <dt class="text-slate-600">Посещений</dt>
                    <dd class="m-0 text-right font-medium text-slate-900">{{ visits }}</dd>
                </div>
                <div class="flex justify-between gap-4">
                    <dt class="text-slate-600">Количество</dt>
                    <dd class="m-0 text-right font-medium text-slate-900">{{ quantity }}</dd>
                </div>
            </dl>
            <div aria-live="polite" class="mt-5 border-t border-slate-200 pt-4">
                <p class="m-0 text-sm text-slate-600">К оплате</p>
                <p class="m-0 mt-1 text-2xl font-semibold text-slate-900">{{ currencyFormatter.format(cost) }}</p>
            </div>
        </aside>
    </div>
</template>

<style scoped>

</style>
